/**
 * CommunityMessageLikeButton
 *
 * Animated heart + intl-formatted count row for a community message, the web
 * port of Flutter's `CommunityMessageItem.messageLikeReaction`
 * (lib/presentation/custom_widgets/community_message_item.dart:304-350).
 *
 * Structural sibling of CommentLikeButton (same like widget, sized down for
 * inline rows). Deviations from that sibling, per the Flutter source:
 * - the unliked heart is tinted `theme.white` (`:338`), not `theme.icon.primary`;
 * - the whole row (heart + count) is tappable — Flutter wraps both in one
 *   `GestureDetector` (`:305-306`), so the count toggles too.
 */

import React, { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AnimatedBubblesButton } from '@/presentation/feature/content/components/AnimatedBubblesButton';
import { useCommunityMessageLike } from '@/presentation/feature/community/hooks/useCommunityMessageLike';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface CommunityMessageLikeButtonProps {
  messageId: string;
  likes: number;
  size: number;
  onToggle: (liked: boolean) => void;
}

export const CommunityMessageLikeButton: React.FC<CommunityMessageLikeButtonProps> = ({
  messageId,
  likes,
  size,
  onToggle,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { isLiked } = useCommunityMessageLike(messageId);

  const handleToggle = useCallback(() => {
    onToggle(!isLiked);
  }, [onToggle, isLiked]);

  // The inner AnimatedBubblesButton stops click propagation, so a heart click
  // fires `handleToggle` via `onTap` only, while a click on the count/row area
  // fires the row's `onClick` only — exactly one toggle per pointer activation.
  const activate = useEnterOrSpaceActivation<HTMLDivElement>(handleToggle);
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Ignore key events bubbling up from the inner heart button — it handles
      // its own native activation. Only toggle when the row itself has focus,
      // so keyboard activation also fires `handleToggle` exactly once.
      if (event.target !== event.currentTarget) return;
      activate(event);
    },
    [activate],
  );

  return (
    <div
      {...qaAttr({
        id: `community-message-like-${messageId}`,
        status: isLiked ? 'liked' : 'unliked',
      })}
      role="button"
      tabIndex={0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: responsive.gap.xs,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <AnimatedBubblesButton
        contentId={messageId}
        isLiked={isLiked}
        size={size}
        likeColor={theme.activeRed}
        unlikeColor={theme.white}
        onTap={handleToggle}
        animationDuration={ANIMATION_CONSTANTS.duration.long}
        circleColor={theme.exclusive}
        iconUrl="/assets/images/heart_fill.png"
        bubbles={[
          { color: theme.activeRed },
          { color: theme.activeRed },
          { color: theme.exclusive },
          { color: theme.activeRed },
          { color: theme.activeRed },
          { color: theme.exclusive },
        ]}
      />
      <span style={{ fontSize: responsive.font.xs, color: theme.text.secondary }}>
        {MathUtils.getReactionsIntlNumber(likes)}
      </span>
    </div>
  );
};
